import Header from "../../components/shared/Header";
import Footer from "../../components/shared/Footer";
import SEOHead from "../../components/shared/SEOHead";
import Breadcrumbs, { generateBreadcrumbSchema } from "../../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Phone, Star } from "lucide-react";
import { createServiceSchema, createFAQSchema } from "../../seo/schemas";

const AreaPage = ({ area, postcode, description, landmarks, testimonial, faqs }) => {
  const breadcrumbs = [
    { label: "Areas We Cover", href: "/areas-we-cover" },
    { label: area, href: `/areas/${area.toLowerCase()}` },
  ];

  const jsonLd = [
    createServiceSchema(
      `Removals ${area}`,
      `Professional removal services in ${area}. House moves, office relocations, storage and specialist transport.`,
      `/areas/${area.toLowerCase()}`
    ),
    createFAQSchema(faqs),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`Removals ${area} | Local Moving Company | Hitech Transit`}
        description={`Trusted removal company in ${area}. House moves, office relocations, storage & specialist transport. Fully insured, free quotes. Call 07346 209593.`}
        canonical={`/areas/${area.toLowerCase()}`}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-20">
        <Breadcrumbs items={breadcrumbs} />

        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                {area} Removals
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Removals in {area}
              </h1>
              <p className="text-slate-300 text-lg mb-8">{description}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/instant-quote" className="btn-primary inline-flex items-center justify-center gap-2" data-testid={`area-${area.toLowerCase()}-quote-btn`}>
                  Get a Free Quote <ArrowRight className="w-5 h-5" />
                </Link>
                <a href="tel:07346209593" className="bg-white/10 backdrop-blur-sm text-white font-semibold px-6 py-3 inline-flex items-center justify-center gap-2 hover:bg-white/20 transition-colors border border-white/20">
                  <Phone className="w-5 h-5" /> 07346 209593
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Answer-first summary for GEO */}
        <section className="section-padding">
          <div className="container-max">
            <div className="max-w-3xl">
              <div className="bg-slate-50 border-l-4 border-[#FF5A1F] p-6 mb-10">
                <p className="text-slate-700 leading-relaxed">
                  <strong>Hitech Transit</strong> is a professional removal company serving {area} and the surrounding {postcode} postcode areas.
                  We offer house removals, office relocations, storage solutions, and specialist moves. Fully insured up to £50,000, with transparent pricing and no hidden fees.
                </p>
              </div>

              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                What We Offer in {area}
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {[
                  { title: "House Removals", desc: "From studio flats to large family homes across " + area, link: "/services/house-removals" },
                  { title: "Office Removals", desc: "Weekend and out-of-hours business relocations", link: "/services/office-removals" },
                  { title: "Storage Solutions", desc: "Secure, climate-controlled storage from £50/week", link: "/services/storage" },
                  { title: "Specialist Moves", desc: "Pianos, antiques, artwork — handled with care", link: "/services/specialist-moves" },
                ].map((service) => (
                  <Link key={service.title} to={service.link} className="flex items-start gap-3 p-4 border border-slate-200 hover:border-[#FF5A1F] transition-colors group">
                    <Check className="w-5 h-5 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-[#0F172A] group-hover:text-[#FF5A1F] transition-colors">{service.title}</h3>
                      <p className="text-sm text-slate-600">{service.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>

              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Why Choose Hitech Transit in {area}
              </h2>
              <ul className="space-y-3 mb-10">
                {[
                  "Local team with deep knowledge of " + area + " streets and access challenges",
                  "Fully insured up to £50,000 as standard",
                  "Transparent, fixed-price quotes — no hidden fees",
                  "Same-day and next-day availability when possible",
                  "Rated 4.9/5 by 500+ customers across Wales and England",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Local knowledge */}
              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Covering {area} and Surrounding Areas
              </h2>
              <p className="text-slate-600 mb-4">
                We regularly serve the {postcode} postcode area and surrounding neighbourhoods including {landmarks}.
                Whether you're moving within {area} or heading further afield, our team knows the best routes
                and can handle any access challenges.
              </p>

              {/* Testimonial */}
              {testimonial && (
                <div className="bg-slate-50 p-6 mb-10">
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map((i) => <Star key={i} className="w-4 h-4 fill-[#FF5A1F] text-[#FF5A1F]" />)}
                  </div>
                  <p className="text-slate-700 italic mb-3">"{testimonial.text}"</p>
                  <p className="text-sm font-semibold text-[#0F172A]">
                    — {testimonial.name}, {testimonial.location}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-padding bg-slate-50">
          <div className="container-max">
            <div className="max-w-3xl">
              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-8">
                Frequently Asked Questions About Removals in {area}
              </h2>
              <div className="space-y-6">
                {faqs.map((faq, i) => (
                  <div key={`faq-${i}`} className="bg-white border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{faq.q}</h3>
                    <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-[#FF5A1F]">
          <div className="container-max text-center">
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white mb-4">
              Ready to Move in {area}?
            </h2>
            <p className="text-white/90 text-lg max-w-xl mx-auto mb-8">
              Get your free, no-obligation quote in under 60 seconds.
            </p>
            <Link to="/instant-quote" className="bg-white text-[#FF5A1F] font-semibold px-8 py-4 inline-flex items-center gap-2 hover:bg-slate-50 transition-colors">
              Get My Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AreaPage;
